import {H5ForecastData, H5ForecastVariable} from 'src/lib/api/h5';
import {
  ApiForecast,
  ApiForecastTimeSeries,
  FlowForecastColumns,
  Source,
  TimeSeriesData,
  TimeSeriesVariables,
} from 'src/lib/api/series';

const FLOW_COLUMNS = new Set<string>(Object.values(FlowForecastColumns));

/** The chart column an H5 flow variable draws as, or undefined for one the chart has no place for. */
export function flowColumnFor(variable: H5ForecastVariable): FlowForecastColumns | undefined {
  const column =
    variable.statistic === 'mean' || variable.statistic === 'deterministic'
      ? FlowForecastColumns.DISCHARGE_MEAN
      : variable.statistic === 'median'
        ? FlowForecastColumns.DISCHARGE_MEDIAN
        : variable.quantile !== undefined
          ? `discharge_q${variable.quantile}`
          : undefined;
  return column !== undefined && FLOW_COLUMNS.has(column)
    ? (column as FlowForecastColumns)
    : undefined;
}

/**
 * An H5 forecast response as the series type the dashboard chart draws, so an H5 section renders
 * through the same chart as a legacy one. `source` is the chart's styling identity for the series,
 * not where the data came from.
 */
export function toForecastTimeSeries(
  data: H5ForecastData,
  {siteId, source}: {siteId: string; source: Source},
  reportUnsupported: (message: string) => void = console.warn
): ApiForecastTimeSeries {
  const columnByVariable = new Map<string, FlowForecastColumns>();
  const variables: TimeSeriesVariables = {};
  for (const variable of data.forecasts.find((frame) => frame.validTimes.length > 0)?.variables ??
    []) {
    const column = flowColumnFor(variable);
    if (column === undefined) {
      reportUnsupported(`Forecast variable ${variable.name} has no chart column`);
      continue;
    }
    columnByVariable.set(variable.name, column);
    variables[column] = {unit: variable.unit};
  }

  const forecasts: ApiForecast[] = data.forecasts
    .filter((frame) => frame.validTimes.length > 0)
    .map((frame) => {
      const values: TimeSeriesData = {};
      for (const [name, column] of columnByVariable) {
        const series = frame.values[name];
        if (!series) {
          reportUnsupported(`Forecast response omitted ${name}`);
          continue;
        }
        values[column] = series;
      }
      return {
        initializationTime: frame.initTime,
        issueTime: frame.issueTime,
        validTimes: frame.validTimes,
        validTimeRange: [frame.validTimes[0], frame.validTimes[frame.validTimes.length - 1]],
        data: values,
      };
    });

  return {
    query: {
      siteId,
      source,
      columns: [...columnByVariable.values()],
      initializationTimes: forecasts.map((forecast) => forecast.initializationTime),
    },
    variables,
    forecasts,
  };
}
